import { faExclamationTriangle as Required } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Grid, Tooltip } from '@material-ui/core';
import _ from 'lodash';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { createEmptyField } from '../../../components/templateEditor/utils';
import { validateCustomAndDefaultFields } from '../../../components/templateEditor/utils/templateValidation';
import api from '../../../infrastructure/api/api';
import appInsights from '../../../infrastructure/appInsights';
import DefaultFields from './default-fields';
import { DefaultFieldsContainer } from './default-fields/DefaultFieldsContainer';
import { Fields } from './fields/index';
import { useStyles } from './styles';
import { Templates } from './templates/index';

const AdminFieldsAndTemplates = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const defaultFieldsContainer = DefaultFieldsContainer.useContainer();

  const [fieldsAndTemplates, setFieldsAndTemplates] = useState({
    customFields: [],
    accountingObjects: [],
    defaultFields: {
      series: createEmptyField(),
      company: createEmptyField(),
      voucherText: createEmptyField(),
      transactionText: createEmptyField(),
    },
  });
  const [refreshDialogTimestamp, setRefreshDialogTimestamp] = useState(
    Date.now()
  );

  useEffect(() => {
    appInsights.trackPageView();

    api.get('fieldsAndTemplates', enqueueSnackbar, t).then(result => {
      if (result !== null) {
        setFieldsAndTemplates(result);
      }
    });
  }, [t, enqueueSnackbar]);

  // eslint-disable-next-line consistent-return
  const saveFieldsAndTemplates = useCallback(() => {
    const newFieldsAndTemplates = {
      customFields: fieldsAndTemplates.customFields.map(cf => ({
        ...cf,
        name: cf.name ? cf.name.trim() : cf.name,
      })),
      accountingObjects: fieldsAndTemplates.accountingObjects,
      defaultFields: fieldsAndTemplates.defaultFields,
    };

    if (!validateCustomAndDefaultFields(newFieldsAndTemplates)) {
      return enqueueSnackbar(t('fieldsAndTemplatesPage.invalidFieldsError'), {
        variant: 'error',
      });
    }

    api
      .post('fieldsAndTemplates', newFieldsAndTemplates, enqueueSnackbar, t)
      .then(() => {
        setRefreshDialogTimestamp(Date.now());

        defaultFieldsContainer.setDirtyState(false);
      });
  }, [enqueueSnackbar, fieldsAndTemplates, t, defaultFieldsContainer]);

  const changeCustomField = useCallback((id, newName) => {
    setFieldsAndTemplates(prevFieldsAndTemplates => {
      const field = prevFieldsAndTemplates.customFields.find(
        f => f.customFieldId === id
      );
      field.name = newName;

      return { ...prevFieldsAndTemplates };
    });
  }, []);

  const removeCustomField = useCallback(
    customFieldToRemove => {
      _.remove(
        fieldsAndTemplates.customFields,
        f => f.customFieldId === customFieldToRemove
      );
      defaultFieldsContainer.setUnmappedField(customFieldToRemove);
      setFieldsAndTemplates({ ...fieldsAndTemplates });
      defaultFieldsContainer.setDirtyState(true);
    },
    [fieldsAndTemplates, defaultFieldsContainer]
  );

  const addCustomField = useCallback(
    customField => {
      const newCustomFields = [...fieldsAndTemplates.customFields];
      if (customField.name !== '') {
        const updatedCustomField = customField;
        updatedCustomField.customFieldId = v4();
        newCustomFields.push(updatedCustomField);

        fieldsAndTemplates.customFields = newCustomFields;
        setFieldsAndTemplates({ ...fieldsAndTemplates });
        defaultFieldsContainer.setDirtyState(true);
      }
    },
    [fieldsAndTemplates, defaultFieldsContainer]
  );

  return (
    <div className={classes.Container}>
      <Grid item xs={12}>
        <h1 className={classes.Title}>
          {t('fieldsAndTemplatesPage.customFields')}
        </h1>
      </Grid>

      <Fields
        addField={addCustomField}
        changeField={changeCustomField}
        removeField={removeCustomField}
        fieldsAndTemplates={fieldsAndTemplates}
      />
      <Grid item xs={12}>
        <h1 className={classes.Title}>
          {t('fieldsAndTemplatesPage.defaultFields')}
        </h1>
      </Grid>

      <Grid item container justifyContent="center" xs={12} spacing={3}>
        <DefaultFields fieldsAndTemplates={fieldsAndTemplates} />
      </Grid>

      <Grid item xs={12}>
        <h1 className={classes.Title}>
          {t('fieldsAndTemplatesPage.templates')}
        </h1>
      </Grid>

      <Grid item container justifyContent="center" xs={8} spacing={3}>
        <Templates refreshDialog={refreshDialogTimestamp} />
      </Grid>

      <Grid
        item
        container
        xs={12}
        justifyContent="flex-end"
        className={classes.StickyFooter}
      >
        {/* Wrapped with div since Tooltip will require ref if custom component is a wrapper around children */}
        {defaultFieldsContainer.dirtyState && (
          <Tooltip
            title={t('fieldsAndTemplatesPage.unsavedChangesWarning')}
            placement="top"
            arrow
          >
            <div className={classes.iconWrapper}>
              <FontAwesomeIcon
                icon={Required}
                className={`${classes.endAdornmentIcon} ${classes.errorIcon}`}
              />
            </div>
          </Tooltip>
        )}
        <Button
          id="saveFieldsAndTemplates"
          variant="contained"
          color="primary"
          title={t('global.buttons.saveAll')}
          onClick={saveFieldsAndTemplates}
          className={classes.SaveAllButton}
          disabled={fieldsAndTemplates.accountingObjects.length !== 8}
        >
          {t('global.buttons.saveAll')}
        </Button>
      </Grid>
      <Grid item xs={4} />
    </div>
  );
};

export default AdminFieldsAndTemplates;
